import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, effect, inject, signal, computed, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent, Subject } from 'rxjs'; // Combine rxjs imports
import { takeUntil, map } from 'rxjs/operators';
import { IconsComponent } from '../../utils/icons/icons.component';
import { MessageContent, WordTimestamps } from '../../models/agent.models'; // Add WordTimestamps

import { extractTags } from './extract-tags';

export interface IWordMetadata {
  word: string;
  index: number;
  isHighlighted?: boolean;
  tag?: string;
}

@Component({
  selector: 'dc-text-highlighter',
  standalone: true,
  imports: [IconsComponent],
  templateUrl: './text-highlighter.html',
  styleUrls: ['./text-highlighter.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextHighlighterComponent implements OnDestroy {
  // Inputs
  public message = input.required<MessageContent>(); // Or input.required<MessageContent>() if always expected
  // Outputs
  readonly playAudio = output<MessageContent>();
  readonly audioCompleted = output<MessageContent>();
  readonly wordClicked = output<{ word: string; index: number; messageId: string }>(); // Output for clicked word with messageId
  // Signal States
  public wordWithMeta = signal<IWordMetadata[]>([]); // Signal for plain text words when no transcription
  public isPlaying = signal(false); // Signal for play state
  public alreadyPlayed = false;

  // Signals State computed from the input message
  public wordsWithMetaState: IWordMetadata[] = [];

  public iconState = computed(() => {
    if (this.isLoading()) {
      return 'isLoading';
    } else if (this.isPlaying()) {
      return 'isPlaying';
    } else if (this.message()?.audioUrl) {
      return 'playable';
    } else {
      return 'idle';
    }
  });

  public isLoading = computed(() => this.message()?.isLoading);

  public shouldPlayAudio = computed(() => !!this.message() && this.message()?.shouldPlayAudio);

  // Computed signal for message text
  messageText = computed(() => {
    const msg = this.message(); // Read the input signal
    return msg?.text || msg?.content || 'N/A';
  });

  classTag = computed(() => this.message()?.tag); // tag can be at message level or word level this is for message level

  // Computed signal for transcription availability
  hasTranscription = computed(() => {
    const hasTranscription = !!this.message()?.transcriptionTimestamps && this.message()?.transcriptionTimestamps.length > 0;
    return hasTranscription;
  });

  private audioElement: HTMLAudioElement | null = null; // Audio element for playback

  private destroy$ = new Subject<void>(); // Cleanup localsubject

  // Inject services
  private destroyRef = inject(DestroyRef);

  constructor() {
    // Effect to react to message changes and re-initialize
    effect(() => {
      const currentMsg = this.message(); // Read the input signal
      // Somethimes message changes but audio is already playing.
      if (currentMsg && !this.isPlaying() && !this.alreadyPlayed) {
        console.log('Cambio en message:', currentMsg);
        this.initializeBasedOnMessage(currentMsg);
        // Check if shouldPlayAudio flag is set
        if (currentMsg.shouldPlayAudio) {
          this.startAudioPlayback();
        }
      } else {
        // this.cleanupAudio();
      }
    });

    // Keep the effect for highlightedWords for now, might be redundant if template binds directly
    effect(() => {
      // some how i need to isolate the reading, becouse reading in main effect is very dangerous since later is set and produces infinite loop
      this.wordsWithMetaState = this.wordWithMeta();
    });
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupAudio();
  }

  private initializeBasedOnMessage(msg: MessageContent): void {
    console.log('Initializing based on message:', msg);
    // Initialize or cleanup audio based on URL presence and change
    if (msg.audioUrl && (!this.audioElement || this.audioElement.src !== msg.audioUrl)) {
      this.initializeAudio(msg.audioUrl); // Pass URL
    } else if (!msg.audioUrl && this.audioElement) {
      this.cleanupAudio();
    } else if (!msg.audioUrl) {
      // No audio URL and no audio element - just log a warning instead of throwing an error
      console.warn('No audioUrl provided in the message. Audio playback will not be available.');
    }

    // Initialize words and sync based on transcription presence
    if (this.hasTranscription()) {
      const wordsAndTimestamps: (IWordMetadata & WordTimestamps)[] = [];

      // NOTE: This merge is only going to work if transcriptionTimestamps and wordsWithMetaState have the same length
      // wordsWithMetaState is initialized here in initializePlainTextWords but transcriptionTimestamps in the father, check later if i have bugs.

      msg?.transcriptionTimestamps?.forEach((word, index) => {
        wordsAndTimestamps.push({ ...word, ...this.wordsWithMetaState[index], index });
      });
      console.log(wordsAndTimestamps);

      this.subcribeToAudioSync(wordsAndTimestamps); // Pass timestamps
    } else {
      this.subscribeToEndAudio();
      this.initializePlainTextWords(msg.text || msg.content || ''); // Initialize plain text words
    }
  }

  private initializeAudio(audioUrl: string): void {
    this.cleanupAudio();
    this.audioElement = new Audio(audioUrl); // Use passed URL
  }

  private subcribeToAudioSync(timestamps: (IWordMetadata & WordTimestamps)[]): void {
    if (!this.audioElement) {
      // Guard against missing audio element
      return;
    }
    this.destroy$.next(); // Signal previous subscriptions to complete

    console.log('Subscribing to audio sync with timestamps:', timestamps);

    fromEvent(this.audioElement, 'timeupdate')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        takeUntil(this.destroy$), // Use manual subject for re-initialization cleanup
        map(() => this.audioElement?.currentTime || 0),
      )
      .subscribe((currentTime) => {
        // Use the passed timestamps array
        const updatedWords = timestamps.map((word, index) => {
          const isHighlighted = currentTime >= word.start - 0.15 && currentTime < word.end + 0.15;
          console.log(isHighlighted, currentTime, word.start, word.end, word.start - 0.15 && currentTime < word.end + 0.15);
          return { word: word.word, index, isHighlighted, tag: word.tag };
        });
        this.wordWithMeta.set(updatedWords);
      });

    // Listen to ended event for cleanup
    fromEvent(this.audioElement, 'ended')
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.destroy$))
      .subscribe(() => {
        // Reset highlighting when audio ends
        const resetWords = this.wordWithMeta().map((word) => ({
          // Read current words signal
          ...word,
          isHighlighted: false,
        }));

        this.wordWithMeta.set(resetWords);

        // Set isPlaying to false when audio finishes
        this.isPlaying.set(false);

        const currentMsg = this.message();
        if (currentMsg) {
          this.cleanupAudio();
          currentMsg.shouldPlayAudio = false;
          this.audioCompleted.emit(currentMsg);
        }
      });
  }

  private subscribeToEndAudio(): void {
    if (!this.audioElement) {
      return;
    }

    fromEvent(this.audioElement, 'ended')
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.destroy$))
      .subscribe(() => {
        // Set isPlaying to false when audio finishes
        this.isPlaying.set(false);

        // Emit audio completed event with the current message
        const currentMsg = this.message();
        if (currentMsg) {
          this.cleanupAudio();

          currentMsg.shouldPlayAudio = false;
          this.audioCompleted.emit(currentMsg);
        }
      });
  }

  /**
   * Clean up audio element and event listeners
   */
  private cleanupAudio(): void {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.removeAttribute('src'); // Use removeAttribute for better cleanup
      this.audioElement = null;
      this.destroy$.next(); // Ensure listeners tied to this audio instance are cleaned up
      // Reset highlighting immediately on cleanup
      const resetWords = this.wordWithMeta().map((word) => ({
        ...word,
        isHighlighted: false,
      }));
      this.wordWithMeta.set(resetWords);
    }
  }

  /**
   * Play or pause the audio
   */
  onPlayMessage(): void {
    console.log('Playing message audio', this.message().messageId);
    const currentMsg = this.message(); // Read signal
    if (this.audioElement) {
      if (this.audioElement.paused) {
        this.startAudioPlayback();
      } else {
        this.audioElement.pause();
      }
    } else if (currentMsg?.audioUrl) {
      // Initialization now happens via effect. If element doesn't exist yet,
      // the effect should create it. Clicking play might need to ensure init runs.
      // For simplicity, let's re-call initializeAudio here if needed,
      // though ideally the effect handles it.
      this.initializeAudio(currentMsg.audioUrl); // Ensure it's created if somehow missed
      this.startAudioPlayback();
    } else if (currentMsg) {
      // If no audio URL is available, emit event to parent component
      this.playAudio.emit(currentMsg); // Emit the message value
    }
  }

  /**
   * Initialize plain text words by splitting the message text
   */
  private initializePlainTextWords(text: string): void {
    const words = extractTags(text);
    const plainWords = words.map((word, index) => ({
      word: word.word,
      index: index,
      isHighlighted: false,
      tag: word.tag,
    }));

    this.wordWithMeta.set(plainWords);
  }

  onWordClick(wordData: { word: string; index: number } | IWordMetadata): void {
    const currentMsg = this.message();
    if (!currentMsg) {
      console.warn('Cannot emit wordClicked: message input is not available.');
      return;
    }
    // Determine the correct data structure based on input type
    const clickedWord =
      'isHighlighted' in wordData
        ? { word: wordData.word, index: wordData.index, messageId: currentMsg.messageId }
        : { ...wordData, messageId: currentMsg.messageId };
    this.wordClicked.emit(clickedWord);
  }

  private startAudioPlayback(): void {
    if (!this.audioElement) return;

    this.audioElement.play().catch((error) => {
      console.error('Error playing audio:', error);
    });
    this.isPlaying.set(true); // Set play state to true
    this.alreadyPlayed = true;
  }
}
